import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Lock, Unlock, Calendar, Dumbbell, CheckCircle, Sparkles, CreditCard } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

import { apiError } from '@/lib/apiError';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BlockDetailModal — vista de un bloque del macroplan.
 *
 * Si está desbloqueado: muestra descripción + fechas + preview de cada
 * workout_day (nombre, foco, duración, n_ejercicios — NO los ejercicios
 * en sí para evitar copia).
 *
 * Si está bloqueado: muestra solo descripción y fechas + CTA de pago
 * adelantado para 1, 3 o 6 bloques con descuentos.
 *
 * Props:
 *   blockNumber  — número del bloque (1..N)
 *   onClose      — callback cierre
 *   onUnlocked   — callback cuando se desbloquea (refresca macroplan)
 */
const BlockDetailModal = ({ blockNumber, onClose, onUnlocked }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [bundleSize, setBundleSize] = useState(1);

  useEffect(() => {
    if (!blockNumber) return;
    setLoading(true);
    axios
      .get(`${API}/methodology/me/macroplan/blocks/${blockNumber}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        toast.error(apiError(err, 'Error al cargar bloque'));
        onClose();
      })
      .finally(() => setLoading(false));
  }, [blockNumber, onClose]);

  const handleUnlock = async () => {
    if (!data) return;
    setPaying(true);
    try {
      // Si compras un bundle, desbloqueas este bloque + los siguientes
      const blocks = [];
      for (let i = 0; i < bundleSize; i++) {
        blocks.push(blockNumber + i);
      }
      const res = await axios.post(`${API}/billing/blocks/checkout`, {
        block_numbers: blocks,
      });
      toast.success(res.data.message || 'Bloques desbloqueados');
      onUnlocked && onUnlocked();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al procesar pago');
    } finally {
      setPaying(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('es-MX', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  // Precios por bundle (igual que el backend, solo display)
  const pricing = {
    1: { total: 199, label: '1 bloque (4 sem)', discount: 0 },
    3: { total: 507, label: '3 bloques (12 sem)', discount: 15 },
    6: { total: 895, label: '6 bloques (24 sem)', discount: 25 },
  };

  return (
    <Dialog open={!!blockNumber} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90dvh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : !data ? null : (
          <>
            <DialogHeader>
              <DialogTitle className="font-teko text-2xl text-white uppercase flex items-center gap-2">
                {data.unlocked ? (
                  <Unlock className="w-6 h-6 text-emerald-400" />
                ) : (
                  <Lock className="w-6 h-6 text-amber-400" />
                )}
                Bloque {data.block.number}: {phaseLabels[data.block.phase] || data.block.phase}
              </DialogTitle>
              <p className="text-zinc-500 text-xs">
                {data.block.is_current ? 'En curso' : data.block.is_past ? 'Completado' : 'Próximo'}
                {' · '}
                {data.block.weeks} semanas
              </p>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Descripción / foco */}
              <Card className="card-default">
                <CardContent className="p-4 space-y-2">
                  <p className="text-zinc-400 text-xs uppercase tracking-wider">Foco del bloque</p>
                  <p className="text-zinc-200 text-sm">{data.block.focus}</p>
                </CardContent>
              </Card>

              {/* Fechas estimadas */}
              <Card className="card-default">
                <CardContent className="p-4 space-y-2">
                  <p className="text-zinc-400 text-xs uppercase tracking-wider flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Fechas estimadas
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-zinc-500 text-xs">Inicio</p>
                      <p className="text-zinc-200">{fmtDate(data.block.estimated_start)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500 text-xs">Cierre</p>
                      <p className="text-zinc-200">{fmtDate(data.block.estimated_end)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Si desbloqueado: vista previa del plan (sin ejercicios) */}
              {data.unlocked && data.workout_preview ? (
                <Card className="card-default border-emerald-500/20">
                  <CardContent className="p-4">
                    <p className="text-emerald-300 text-xs uppercase tracking-wider mb-3 flex items-center gap-1">
                      <Dumbbell className="w-3 h-3" /> Estructura semanal
                    </p>
                    <p className="text-zinc-400 text-xs mb-3">
                      {data.workout_preview.title} · {data.workout_preview.days_per_week} días/sem
                    </p>
                    <div className="space-y-2">
                      {data.workout_preview.workout_days.map((wd) => (
                        <div key={wd.day_number} className="p-2 bg-zinc-900/40 rounded-sm">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-zinc-200 text-sm font-medium">
                              Día {wd.day_number}: {wd.day_name}
                            </p>
                            <span className="text-zinc-500 text-xs">
                              {wd.estimated_duration_min ? `${wd.estimated_duration_min} min` : '—'}
                            </span>
                          </div>
                          <p className="text-zinc-500 text-xs">
                            {wd.focus} · {wd.total_exercises} ejercicios
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-zinc-500 text-xs italic mt-3">
                      Verás los ejercicios completos al iniciar cada día desde el calendario.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                /* Bloqueado: CTA de pago */
                <Card className="card-default border-amber-500/30 bg-amber-500/5">
                  <CardContent className="p-4 space-y-3">
                    <p className="text-amber-300 text-xs uppercase tracking-wider flex items-center gap-1">
                      <Lock className="w-3 h-3" /> Bloque bloqueado
                    </p>
                    <p className="text-zinc-300 text-sm">
                      Para acceder a los ejercicios y empezar este bloque,
                      necesitas activar tu acceso. Puedes pagar por bloque,
                      o adelantar varios bloques con descuento.
                    </p>

                    <div className="space-y-2">
                      <p className="text-zinc-400 text-xs uppercase tracking-wider">
                        ¿Cuántos bloques quieres activar?
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {[1, 3, 6].map((n) => {
                          const p = pricing[n];
                          const selected = bundleSize === n;
                          return (
                            <button
                              key={n}
                              onClick={() => setBundleSize(n)}
                              className={`p-3 rounded-sm border text-left ${
                                selected
                                  ? 'border-amber-500 bg-amber-500/15'
                                  : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'
                              }`}
                            >
                              <p className={`text-xs font-bold ${selected ? 'text-amber-300' : 'text-zinc-400'}`}>
                                {p.label}
                              </p>
                              <p className={`font-teko text-2xl ${selected ? 'text-white' : 'text-zinc-300'}`}>
                                ${p.total}
                                <span className="text-xs text-zinc-500 ml-1">MXN</span>
                              </p>
                              {p.discount > 0 && (
                                <p className="text-emerald-400 text-[10px] mt-0.5">
                                  -{p.discount}% off
                                </p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="p-3 bg-primary/5 border border-primary/20 rounded-sm flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-primary text-xs">
                        <strong>Modo simulación:</strong> al activar, el bloque
                        se desbloquea de inmediato sin cobro real. Esto te
                        permite probar el flujo completo antes de conectar Stripe.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={onClose} disabled={paying}>
                Cerrar
              </Button>
              {!data.unlocked && (
                <Button onClick={handleUnlock} className="btn-primary" disabled={paying}>
                  {paying
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <CreditCard className="w-4 h-4 mr-2" />}
                  Activar bloque{bundleSize > 1 ? `s (${bundleSize})` : ''}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const phaseLabels = {
  adaptation: 'Adaptación',
  deficit_main: 'Déficit principal',
  acceleration: 'Aceleración',
  stabilization: 'Estabilización',
  baseline: 'Línea base',
  hypertrophy: 'Hipertrofia',
  intensification: 'Intensificación',
  consolidation: 'Consolidación',
  foundation: 'Fundamento',
  strength: 'Fuerza',
  peak: 'Peak',
  accumulation: 'Acumulación',
  realization: 'Realización',
  deload: 'Descarga',
  habit_build: 'Construcción de hábitos',
};

export default BlockDetailModal;
